import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdDevices, MdOutlineSecurity, MdOutlineLocalShipping, MdOutlineVerified, MdAutoAwesome, MdLogin } from 'react-icons/md';

export default function VortexLayout({ p }) {
  const T = p.storeTheme || {};
  const buttonColor = T.buttonColor || "#d946ef"; // Neon Purple/Magenta
  const primaryColor = T.primaryColor || "#6366f1"; // Indigo
  const secondaryColor = T.secondaryColor || "#06b6d4"; // Cyan

  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Next-Gen Creative Agency & Shop",
    title: "Vortex. Pure Innovation.",
    subtitle: "Explore boundary-pushing designs, custom neon accessories, and futuristic collectibles crafted by digital architects.",
    primaryCta: "Enter the Vortex",
    secondaryCta: "Studio Specs",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#03000a] text-slate-200 antialiased selection:bg-[#d946ef]/30 selection:text-white" style={{ fontFamily: T.fontFamily || "Outfit, sans-serif" }}>
      {/* Neo-scrolling top banner */}
      <div className="w-full bg-gradient-to-r from-[#6366f1] via-[#d946ef] to-[#06b6d4] py-2 px-4 text-center text-xs font-bold uppercase tracking-widest text-white shadow-[0_4px_20px_rgba(217,70,239,0.25)]">
        <span className="inline-flex items-center gap-1.5 animate-pulse">
          <MdAutoAwesome className="text-white text-sm" /> VORTEX CONCEPTS: Experience digital innovation at the speed of thought.
        </span>
      </div>

      {/* Glassmorphic Cyber Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#03000a]/90 backdrop-blur-md shadow-[0_8px_32px_rgba(217,70,239,0.08)] border-b border-[#c084fc]/15" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-[#d946ef]/40 shadow-[0_0_20px_rgba(217,70,239,0.3)]" size="h-9 w-9" />
            <div>
              <p className="text-base font-extrabold tracking-tight text-white">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#06b6d4] font-black tracking-widest uppercase">{p.storeInfo?.businessCategory || "Creative Studio"}</p>
            </div>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Explore Store"],
              ["details", "Studio Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-bold tracking-widest uppercase transition-all hover:text-[#d946ef] ${
                  activePage === page 
                    ? "text-[#d946ef] border-b-2 border-[#d946ef] pb-1 shadow-[0_2px_10px_rgba(217,70,239,0.2)]" 
                    : "text-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="hidden md:block w-48" />

            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full hover:bg-slate-900/60 text-slate-300 transition-colors" title="Wishlist">
              <FaHeart className="text-base hover:scale-110 transition-transform" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#d946ef] shadow-[0_0_10px_rgba(217,70,239,0.5)]">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />

            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-full hover:bg-slate-900 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#03000a]/95 backdrop-blur-lg border-b border-[#c084fc]/15 shadow-2xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Explore Store"],
              ["details", "Studio Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-sm font-bold tracking-wider uppercase border-b border-slate-900 ${
                  activePage === page ? "text-[#d946ef] font-black" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}

            {/* Login Options for Mobile */}
            {!p.userProfile ? (
              <button
                onClick={() => {
                  p.handleOpenLogin();
                  setMobileMenuOpen(false);
                }}
                className="py-2 text-left text-sm font-bold tracking-wider uppercase border-b border-slate-900 flex items-center gap-2 text-[#d946ef]"
              >
                <MdLogin className="text-lg" />
                Login / Sign Up
              </button>
            ) : (
              <div className="py-2 border-b border-slate-900 flex flex-col gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#d946ef] flex items-center justify-center text-xs font-black text-white shadow-[0_0_15px_rgba(217,70,239,0.4)]">
                    {p.userProfile?.name ? p.userProfile.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-white uppercase tracking-wider">{p.userProfile?.name}</p>
                    <p className="text-[10px] text-slate-400">{p.userProfile?.customer_mobile}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => {
                      p.handleViewProfile();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded transition-colors border border-[#c084fc]/15"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      p.handleViewOrders();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded transition-colors border border-[#c084fc]/15"
                  >
                    Orders
                  </button>
                  <button
                    onClick={() => {
                      p.handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-[#d946ef] hover:bg-[#b030c0] px-3 py-1.5 rounded transition-colors ml-auto shadow-sm"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Cinematic Cyber Hero Section */}
            <section className="w-full max-w-6xl mx-auto px-6 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left relative">
              <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#6366f1]/10 rounded-full blur-[100px] pointer-events-none" />
              <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-64 h-64 bg-[#d946ef]/10 rounded-full blur-[100px] pointer-events-none" />

              <div className="flex flex-col items-start pr-0 md:pr-4 relative z-10">
                <span className="text-[#06b6d4] text-xs font-black tracking-[0.25em] uppercase mb-4 py-1.5 px-3 rounded-full bg-[#06b6d4]/10 border border-[#06b6d4]/20">
                  <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                </span>
                <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-white leading-none mb-6">
                  <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                </h1>
                <p className="text-sm md:text-base text-slate-400 font-medium leading-relaxed mb-9 max-w-lg">
                  <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                </p>
                <div className="flex gap-4">
                  <button 
                    onClick={() => navigateTo("shop")}
                    className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest text-white hover:opacity-90 active:scale-97 transition-all shadow-[0_0_30px_rgba(217,70,239,0.35)]"
                    style={{ backgroundColor: buttonColor }}
                  >
                    <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                  </button>
                  <button 
                    onClick={() => {
                      p.setActiveTab("details");
                      navigateTo("about");
                    }}
                    className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest border border-slate-700 hover:border-[#06b6d4] hover:text-white text-slate-300 transition-all bg-[#0a0614]/40"
                  >
                    <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                  </button>
                </div>
              </div>

              {/* Visual Showcase with Cyber-Glow */}
              <div className="w-full rounded-[2.5rem] overflow-hidden bg-slate-950/80 border border-[#c084fc]/20 shadow-[0_20px_50px_rgba(217,70,239,0.06)] p-3 relative group">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#6366f1]/20 via-[#d946ef]/20 to-[#06b6d4]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-[2.3rem]" />
                <HeroImage p={p} themeId="vortex" className="h-[320px] md:h-[450px] rounded-[2.2rem] overflow-hidden transition-transform duration-700 group-hover:scale-[1.02]" />
              </div>
            </section>

            {/* Creative Engineering Spotlight */}
            <section className="bg-[#080214] border-y border-[#c084fc]/10 py-20 relative overflow-hidden">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-[#6366f1]/5 to-[#d946ef]/5 rounded-full blur-[120px] pointer-events-none" />

              <div className="max-w-6xl mx-auto px-6 relative z-10">
                <div className="text-center mb-16">
                  <span className="text-xs uppercase tracking-[0.25em] font-black text-[#d946ef]">Aesthetic Grid</span>
                  <h2 className="text-4xl font-extrabold tracking-tight text-white mt-2">Bending Digital Horizons</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                  {[
                    {
                      title: "Neon Glassmorphism",
                      desc: "Frosted glass container aesthetics featuring customizable neon drop shadows and glowing border lines.",
                      tag: "Dynamic UI",
                      color: "text-[#d946ef] bg-[#d946ef]/10 border-[#d946ef]/20"
                    },
                    {
                      title: "Cybernetic Performance",
                      desc: "Responsive grid layouts engineered for rapid rendering, high image fidelity, and buttery smooth transitions.",
                      tag: "Speed Optimized",
                      color: "text-[#06b6d4] bg-[#06b6d4]/10 border-[#06b6d4]/20"
                    },
                    {
                      title: "Infinite Customization",
                      desc: "Easily adjust primary palettes, typeface themes, button rounding, and border layouts dynamically.",
                      tag: "Flexible",
                      color: "text-[#6366f1] bg-[#6366f1]/10 border-[#6366f1]/20"
                    }
                  ].map((spec, i) => (
                    <div key={i} className="bg-[#0d071d]/60 border border-[#c084fc]/10 rounded-[2rem] p-8 flex flex-col justify-between hover:border-[#d946ef]/40 transition-all duration-500 hover:translate-y-[-4px] group">
                      <div>
                        <span className={`inline-block px-3 py-1 rounded-full text-[9px] font-black tracking-wider uppercase mb-5 border ${spec.color}`}>
                          {spec.tag}
                        </span>
                        <h4 className="text-xl font-bold mt-1 text-white group-hover:text-[#d946ef] transition-colors">{spec.title}</h4>
                        <p className="text-xs text-slate-400 mt-3 leading-relaxed">{spec.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Premium Badges */}
            <section className="max-w-6xl mx-auto px-6 py-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Secure Checkout", icon: <MdOutlineSecurity className="text-2xl text-[#d946ef]" />, desc: "Fully encrypted processing" },
                  { title: "Curated Showcase", icon: <MdOutlineVerified className="text-2xl text-[#06b6d4]" />, desc: "High quality premium catalog" },
                  { title: "Insured Courier", icon: <MdOutlineLocalShipping className="text-2xl text-[#6366f1]" />, desc: "Tracked shipping globally" },
                  { title: "Studio Support", icon: <MdDevices className="text-2xl text-[#d946ef]" />, desc: "Responsive help desk support" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-5 bg-[#0a0418] border border-[#c084fc]/10 rounded-2xl hover:border-[#06b6d4]/30 transition-colors">
                    <div className="p-3 rounded-full bg-[#120926] mb-3.5 shadow-inner">{b.icon}</div>
                    <p className="text-xs font-bold text-white uppercase tracking-wider">{b.title}</p>
                    <p className="text-[10px] text-slate-400 mt-1.5">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog Section */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-16 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06b6d4]">EXHIBIT CATALOG</span>
              <h3 className="text-4xl font-extrabold text-white mt-1.5">Creative Offerings</h3>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-slate-700 bg-[#0a0614] hover:bg-slate-900/60 hover:border-[#d946ef] text-xs font-bold text-slate-200 transition-all uppercase tracking-wider"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-full border border-slate-800 bg-[#0a0614] p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-5 py-2 rounded-full text-xs font-bold transition-all uppercase tracking-wider whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: buttonColor, color: "#fff", boxShadow: "0 0 15px rgba(217,70,239,0.3)" } : { color: "#94a3b8" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} dark={true} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} dark={true} />
    </div>
  );
}
